import { v4 as uuidv4 } from "uuid";

import api from "../utils/api";
import { isGuid } from "../utils/string";
import { LOGOUT } from "./types";
import { send as sendAlert } from "./alert";
import { save as saveCourt } from "./courts";

const fail = (error, fallback) => ({
  success: false,
  error: error?.response?.data?.error || error?.response?.data || { message: error?.message || fallback },
});

const hasAddressData = (address = {}) => [
  "line1",
  "line2",
  "city",
  "state",
  "county",
  "zipCode",
].some((field) => String(address?.[field] ?? "").trim() !== "");

const normalizeAddressCountry = (address) => {
  if (!address) return null;
  return {
    ...address,
    country: address.country === null ? null : String(address.country || "US").trim().toUpperCase(),
  };
};

const courtPayload = (court = {}) => {
  const payload = {
    ...court,
    uuid: isGuid(court.uuid) ? court.uuid : uuidv4(),
  };

  const incomingAddressUuid = isGuid(court.addressUuid)
    ? court.addressUuid
    : isGuid(court.Address?.uuid)
      ? court.Address.uuid
      : null;

  payload.addressUuid = incomingAddressUuid || uuidv4();

  if (hasAddressData(court.Address)) {
    payload.Address = normalizeAddressCountry({
      ...court.Address,
      uuid: payload.addressUuid,
    });
  } else {
    payload.Address = null;
  }

  return payload;
};

export const analyze = ({ file, dataStart = { row: 1, column: 1 } }) => async (dispatch) => {
  try {
    const form = new FormData();
    form.append("file", file);
    form.append("dataStartRow", String(dataStart?.row || 1));
    form.append("dataStartColumn", String(dataStart?.column || 1));

    const res = await api.post("/staff/clocks/import/analyze", form, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return res.data;
  } catch (error) {
    if (error?.response?.status === 401) dispatch({ type: LOGOUT });
    const result = fail(error, "Unable to analyze the import file.");
    dispatch(sendAlert(result.error?.message || "Unable to analyze the import file.", "error"));
    return result;
  }
};

export const apply = ({ importType, rows = [], selectedRowIds = [] }) => async (dispatch) => {
  if (importType !== "courts") {
    return { success: false, error: { message: "Only Courts source-data imports are currently supported." } };
  }

  if (!selectedRowIds.length) {
    return { success: false, error: { message: "Select at least one valid row to import." } };
  }

  const selected = rows.filter((row) => selectedRowIds.includes(row.importRowId) && !row.skipImport);
  const ignored = rows.filter((row) => row.skipImport).length;
  if (!selected.length) {
    return { success: false, error: { message: "Select at least one valid row to import." } };
  }

  const summary = {
    total: rows.length,
    selected: selected.length,
    imported: 0,
    skipped: rows.length - selected.length,
    failed: 0,
    ignored,
  };
  const results = [];

  for (const row of selected) {
    if (row.status === "error") {
      summary.skipped += 1;
      results.push({
        importRowId: row.importRowId,
        rowIndex: row.rowIndex,
        success: false,
        skipped: true,
        error: { message: "Row has blocking validation errors." },
      });
      continue;
    }

    try {
      const item = await dispatch(saveCourt(courtPayload(row.proposed || {})));
      summary.imported += 1;
      results.push({
        importRowId: row.importRowId,
        rowIndex: row.rowIndex,
        success: true,
        item,
      });
    } catch (error) {
      summary.failed += 1;
      results.push({
        importRowId: row.importRowId,
        rowIndex: row.rowIndex,
        success: false,
        error: error?.response?.data?.error || error?.response?.data || { message: error?.message || "Unable to import row." },
      });
    }
  }

  if (summary.imported > 0) {
    const label = summary.imported === 1 ? "court" : "courts";
    dispatch(sendAlert(`${summary.imported} ${label} imported.`, summary.failed ? "warning" : "success"));
  } else if (summary.failed > 0) {
    dispatch(sendAlert("No courts were imported.", "error"));
  }

  return {
    success: true,
    summary,
    results,
  };
};
